import { NgIf } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { Boondock, ILandType } from '@curbnturf/entities';
import { ITerrainRatingForm } from '@curbnturf/form-helpers';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { IonicModule, LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { RatingAvailabilityToggleComponent } from '../rating-availability-toggle/rating-availability-toggle.component';

@Component({
  selector: 'curbnturf-rating-boondock-terrain',
  templateUrl: './rating-boondock-terrain.component.html',
  styleUrls: ['./rating-boondock-terrain.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    IonicModule,
    NgIf,
    RatingAvailabilityToggleComponent,
    ReactiveFormsModule,
    RouterModule,
    SiteSvgsComponent,
  ],
})
export class RatingBoondockTerrainComponent implements OnInit {
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  @Output() continue: EventEmitter<void> = new EventEmitter<void>();

  @Output() skip: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;
  caretakerSites: number[];

  terrainForm: FormGroup<ITerrainRatingForm>;

  get isCaretaker() {
    return !!(this.boondockId && this.caretakerSites.includes(this.boondockId));
  }

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private loadingController: LoadingController,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnInit(): void {
    this.terrainForm = this.fb.group({
      backyard: '',
      beach: '',
      brewery: '',
      canyon: '',
      coastal: '',
      curbside: '',
      desert: '',
      distillery: '',
      driveway: '',
      farmland: '',
      forest: '',
      hotSprings: '',
      lake: '',
      mountains: '',
      orchard: '',
      plains: '',
      river: '',
      suburban: '',
      urban: '',
      waterfall: '',
      wetlands: '',
      winery: '',
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.ratingFacade.caretakerSites$.subscribe((sites) => {
        this.caretakerSites = sites;
      }),
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
          this.populateFields(boondock);
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  populateFields(boondock: Boondock) {
    if (this.isCaretaker) {
      this.terrainForm.patchValue({
        backyard: boondock.landTypes?.find((el) => el === 'backyard') ? 'yes' : 'no',
        beach: boondock.landTypes?.find((el) => el === 'beach') ? 'yes' : 'no',
        brewery: boondock.landTypes?.find((el) => el === 'brewery') ? 'yes' : 'no',
        canyon: boondock.landTypes?.find((el) => el === 'canyon') ? 'yes' : 'no',
        coastal: boondock.landTypes?.find((el) => el === 'coastal') ? 'yes' : 'no',
        curbside: boondock.landTypes?.find((el) => el === 'curbside') ? 'yes' : 'no',
        desert: boondock.landTypes?.find((el) => el === 'desert') ? 'yes' : 'no',
        distillery: boondock.landTypes?.find((el) => el === 'distillery') ? 'yes' : 'no',
        driveway: boondock.landTypes?.find((el) => el === 'driveway') ? 'yes' : 'no',
        farmland: boondock.landTypes?.find((el) => el === 'farm') ? 'yes' : 'no',
        forest: boondock.landTypes?.find((el) => el === 'forest') ? 'yes' : 'no',
        hotSprings: boondock.landTypes?.find((el) => el === 'hotSpring') ? 'yes' : 'no',
        lake: boondock.landTypes?.find((el) => el === 'lake') ? 'yes' : 'no',
        mountains: boondock.landTypes?.find((el) => el === 'mountains') ? 'yes' : 'no',
        orchard: boondock.landTypes?.find((el) => el === 'orchard') ? 'yes' : 'no',
        plains: boondock.landTypes?.find((el) => el === 'plain') ? 'yes' : 'no',
        river: boondock.landTypes?.find((el) => el === 'stream') ? 'yes' : 'no',
        suburban: boondock.landTypes?.find((el) => el === 'suburban') ? 'yes' : 'no',
        urban: boondock.landTypes?.find((el) => el === 'urban') ? 'yes' : 'no',
        waterfall: boondock.landTypes?.find((el) => el === 'waterfall') ? 'yes' : 'no',
        wetlands: boondock.landTypes?.find((el) => el === 'wetlands') ? 'yes' : 'no',
        winery: boondock.landTypes?.find((el) => el === 'winery') ? 'yes' : 'no',
      });
    }
  }

  async onContinue() {
    const boondock = new Boondock({
      id: this.boondockId,
      landTypes: [],
    });

    for (const terrainKey in this.terrainForm.value) {
      if (this.terrainForm.value.hasOwnProperty(terrainKey)) {
        if ((this.terrainForm.value as any)[terrainKey] === 'yes') {
          boondock.landTypes?.push(terrainKey as ILandType['type']);
        }
      }
    }

    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();

        // Emit event for web and navigate for app.
        if (this.appContext) {
          if (this.isCaretaker) {
            await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'conditions']);
          } else {
            await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'complete']);
          }
        } else {
          this.continue.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      boondockId: this.boondockId,
      boondock,
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      if (this.isCaretaker) {
        await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'conditions']);
      } else {
        await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'complete']);
      }
    } else {
      this.skip.emit();
    }
  }
}
